import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Award, Users, TrendingUp, MapPin, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo, services, products, projects, testimonials } from '../mockData';
import HeroBannerSlider from '../components/HeroBannerSlider';
import InfluencerSection from '../components/InfluencerSection';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner Slider */}
      <HeroBannerSlider />

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Award, label: 'Years Experience', value: '15+' },
              { icon: Users, label: 'Happy Clients', value: '500+' },
              { icon: CheckCircle, label: 'Projects Done', value: '800+' },
              { icon: TrendingUp, label: 'Customer Satisfaction', value: '98%' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <stat.icon className="w-12 h-12 mx-auto text-amber-600 mb-3" />
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section - SEO Optimized */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Interior Design Services in Delhi & East Delhi
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Complete interior solutions for homes and offices - from custom designs to Vista Fashion blinds
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.slice(0, 6).map((service) => (
              <Card key={service.id} className="group hover:shadow-xl transition-all duration-300 border-none bg-white">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <div className="w-8 h-8 bg-amber-600 rounded-lg"></div>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-amber-600 text-amber-700 hover:bg-amber-50">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trusted by Influencers Section */}
      <InfluencerSection />

      {/* Featured Products - SEO Optimized */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Window Blinds, Curtains & Flooring in Delhi
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Authorized Vista Fashion dealer - Premium quality products for your home
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, 4).map((product) => (
              <Card key={product.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300 border-none">
                <div className="relative overflow-hidden aspect-square">
                  <img
                    src={product.image}
                    alt={`${product.name} - ${product.category} in Delhi`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
                <CardContent className="p-4">
                  <div className="text-xs text-amber-600 font-medium mb-1">{product.category}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{product.description}</p>
                  <Button size="sm" variant="outline" className="w-full border-amber-600 text-amber-700 hover:bg-amber-50">
                    Request Quote
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                View All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Preview - Minimal Curated Selection */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Selected Works - Interior Projects in Delhi
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Showcasing our expertise in residential and commercial interior design
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.slice(0, 3).map((project) => (
              <div key={project.id} className="group relative overflow-hidden rounded-xl aspect-[4/3]">
                <img
                  src={project.image}
                  alt={`${project.title} - Interior Design Project in Delhi`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6">
                  <div className="text-xs text-amber-400 font-medium mb-2">{project.category}</div>
                  <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                  <p className="text-sm text-gray-300">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/portfolio">
              <Button size="lg" variant="outline" className="border-2 border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-gray-900">
                View More Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-none shadow-lg">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-amber-500 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.location}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Local SEO Section - Areas Served */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serving Delhi & Nearby Areas
            </h2>
            <p className="text-lg text-gray-600">
              Professional interior design services across East Delhi and surrounding regions
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              'East Delhi',
              'Jagat Puri',
              'Preet Vihar',
              'Mayur Vihar',
              'Laxmi Nagar',
              'Shahdara',
              'Vivek Vihar',
              'Krishna Nagar',
              'Dilshad Garden',
              'Noida'
            ].map((area, index) => (
              <div className="flex items-start space-x-3 p-3 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-700">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today for expert interior design services
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${businessInfo.phones[0]}`}>
              <Button size="lg" variant="secondary" className="bg-white text-amber-700 hover:bg-gray-100 px-8 py-6 text-lg">
                <Phone className="w-5 h-5 mr-2" />
                {businessInfo.phones[0]}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
